'use client';

import { useCallback, useMemo, useSyncExternalStore } from 'react';

export type FrecencyEntryType =
  | 'product'
  | 'thread'
  | 'project'
  // @deprecated legacy collections — no new entries are recorded; kept so stale
  // localStorage entries of this type still parse/render instead of crashing.
  | 'collection'
  | 'avatar'
  | 'aesthetic'
  | 'member'
  | 'workspace'
  | 'navigation'
  | 'action';

export type FrecencyMeta = {
  label: string;
  type: FrecencyEntryType;
  /** Entries with an href can be re-run from the Recent section. */
  href?: string;
};

export type FrecencyEntry = {
  key: string;
  count: number;
  lastUsedAt: number;
  meta: FrecencyMeta;
};

const STORAGE_VERSION = 'v1';
const MAX_ENTRIES = 200;
const EVICT_TO = 180;
const HALF_LIFE_DAYS = 14;
const EMPTY: FrecencyEntry[] = [];

const cache = new Map<string, FrecencyEntry[]>();
const listeners = new Map<string, Set<() => void>>();
let storageListenerAttached = false;

function storageKeyFor(userId: string, orgSlug?: string): string {
  return `imai.cmdk.frecency.${STORAGE_VERSION}.${userId}.${orgSlug ?? 'personal'}`;
}

function readEntries(storageKey: string): FrecencyEntry[] {
  const cached = cache.get(storageKey);
  if (cached) return cached;
  if (typeof window === 'undefined') return EMPTY;
  let entries: FrecencyEntry[] = EMPTY;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        entries = parsed.filter(
          (entry): entry is FrecencyEntry =>
            typeof entry === 'object' &&
            entry !== null &&
            typeof (entry as FrecencyEntry).key === 'string' &&
            typeof (entry as FrecencyEntry).count === 'number' &&
            typeof (entry as FrecencyEntry).lastUsedAt === 'number'
        );
      }
    }
  } catch {
    entries = EMPTY;
  }
  cache.set(storageKey, entries);
  return entries;
}

function emit(storageKey: string): void {
  for (const listener of listeners.get(storageKey) ?? []) {
    listener();
  }
}

function writeEntries(storageKey: string, entries: FrecencyEntry[]): void {
  cache.set(storageKey, entries);
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(entries));
  } catch {
    // Quota/permission failures degrade to in-memory frecency.
  }
  emit(storageKey);
}

export function scoreEntry(entry: FrecencyEntry, now: number): number {
  const ageDays = Math.max(0, now - entry.lastUsedAt) / 86_400_000;
  return entry.count * Math.pow(0.5, ageDays / HALF_LIFE_DAYS);
}

function attachStorageListener(): void {
  if (storageListenerAttached || typeof window === 'undefined') return;
  storageListenerAttached = true;
  window.addEventListener('storage', (event) => {
    if (!event.key || !cache.has(event.key)) return;
    cache.delete(event.key);
    emit(event.key);
  });
}

/**
 * localStorage-backed frecency for the ⌘K menu, scoped per user + workspace.
 * SSR-safe (empty server snapshot) with cross-tab sync via storage events.
 */
export function useFrecency({
  userId,
  orgSlug,
}: {
  userId?: string;
  orgSlug?: string;
}) {
  const storageKey = userId ? storageKeyFor(userId, orgSlug) : null;

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!storageKey) return () => {};
      attachStorageListener();
      const set = listeners.get(storageKey) ?? new Set();
      set.add(onChange);
      listeners.set(storageKey, set);
      return () => {
        set.delete(onChange);
      };
    },
    [storageKey]
  );

  const entries = useSyncExternalStore(
    subscribe,
    () => (storageKey ? readEntries(storageKey) : EMPTY),
    () => EMPTY
  );

  const record = useCallback(
    (key: string, meta: FrecencyMeta) => {
      if (!storageKey) return;
      const now = Date.now();
      const current = readEntries(storageKey);
      const existing = current.find((entry) => entry.key === key);
      let next: FrecencyEntry[];
      if (existing) {
        next = current.map((entry) =>
          entry.key === key
            ? { ...entry, count: entry.count + 1, lastUsedAt: now, meta }
            : entry
        );
      } else {
        next = [...current, { key, count: 1, lastUsedAt: now, meta }];
      }
      if (next.length > MAX_ENTRIES) {
        next = [...next]
          .sort((a, b) => scoreEntry(b, now) - scoreEntry(a, now))
          .slice(0, EVICT_TO);
      }
      writeEntries(storageKey, next);
    },
    [storageKey]
  );

  // Score ordering is invariant to the choice of "now" (the ratio of two
  // scores only depends on their count and lastUsedAt difference), so use
  // the newest entry as the reference clock — deterministic, keeps render pure.
  const scoreByKey = useMemo(() => {
    const referenceNow = entries.reduce(
      (max, entry) => Math.max(max, entry.lastUsedAt),
      0
    );
    const map = new Map<string, number>();
    for (const entry of entries) {
      map.set(entry.key, scoreEntry(entry, referenceNow));
    }
    return map;
  }, [entries]);

  const score = useCallback(
    (key: string) => scoreByKey.get(key) ?? 0,
    [scoreByKey]
  );

  const topEntries = useCallback(
    (limit = 5, filter?: (entry: FrecencyEntry) => boolean) => {
      return [...entries]
        .filter((entry) => (filter ? filter(entry) : true))
        .sort(
          (a, b) => (scoreByKey.get(b.key) ?? 0) - (scoreByKey.get(a.key) ?? 0)
        )
        .slice(0, limit);
    },
    [entries, scoreByKey]
  );

  return { record, score, topEntries };
}

/** Stable re-sort: frecency score desc, then original (server) order. */
export function boostByFrecency<T>(
  items: T[],
  keyOf: (item: T) => string,
  score: (key: string) => number
): T[] {
  if (items.length < 2) return items;
  return items
    .map((item, index) => ({ item, index, score: score(keyOf(item)) }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ item }) => item);
}
